import { MAX_STAKE_AMOUNT, stakePolicyKindSchema, stakeUnitSchema } from '@gamble-chess/contracts';
import type { JSX } from 'react';

import { policyKindLabels, unitLabels } from '../format.ts';
import type { StakePolicyDraft } from '../stakePolicyDraft.ts';
import { Field } from '../ui/Field.tsx';
import { fieldGridClass, hintClass, inputClass, legendClass } from '../ui/styles.ts';

export interface StakePolicyFieldsProps {
  readonly value: StakePolicyDraft;
  readonly onChange: (value: StakePolicyDraft) => void;
}

/** The stake of a rule or a wager: how much, of what, and whether ratings scale it. */
export function StakePolicyFields({ value, onChange }: StakePolicyFieldsProps): JSX.Element {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className={legendClass}>Stake</legend>
      <div className={fieldGridClass}>
        <Field label="Amount">
          {(id) => (
            <input
              id={id}
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_STAKE_AMOUNT}
              step={1}
              required
              value={value.amount}
              onChange={(event) => {
                onChange({ ...value, amount: event.target.value });
              }}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Unit">
          {(id) => (
            <select
              id={id}
              value={value.unit}
              onChange={(event) => {
                const parsed = stakeUnitSchema.safeParse(event.target.value);
                if (parsed.success) {
                  onChange({ ...value, unit: parsed.data });
                }
              }}
              className={inputClass}
            >
              {stakeUnitSchema.options.map((unit) => (
                <option key={unit} value={unit}>
                  {unitLabels[unit]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Stake type">
          {(id) => (
            <select
              id={id}
              value={value.kind}
              onChange={(event) => {
                const parsed = stakePolicyKindSchema.safeParse(event.target.value);
                if (parsed.success) {
                  onChange({ ...value, kind: parsed.data });
                }
              }}
              className={inputClass}
            >
              {stakePolicyKindSchema.options.map((kind) => (
                <option key={kind} value={kind}>
                  {policyKindLabels[kind]}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>
      {value.kind === 'rating-tiered' ? (
        <p className={hintClass}>
          Upsets weigh more: losing to a much lower-rated player costs more, losing to a much
          stronger one costs less. The exact bands are shown once it is saved.
        </p>
      ) : null}
    </fieldset>
  );
}
